import { Avatar, Badge, Box, HStack, Icon, Text } from '@chakra-ui/react'
import { ButtonLink } from '@src/components/sco/ButtonLink'
import { TextSpan } from '@src/components/sco/TextSpan'
import { ChatSiteNavBarItem } from '@src/components/sco/nav/ScoSiteNavBar/ChatSiteNavBarItem'
import { SiteNavItem } from '@src/components/sco/nav/ScoSiteNavBar/index'
import { ScoSubscription, ScoUser } from '@src/graphql-types'
import { sortBy } from 'lodash'
import moment from 'moment'
import React from 'react'
import { LuClock } from 'react-icons/lu'

const notificationCount = 0

export const secondaryNavItems = ({
  getBreakpointValue,
  currentUser,
  pathname,
}: {
  getBreakpointValue: any
  currentUser?: ScoUser
  pathname?: string
}): Array<SiteNavItem> => {
  let userHasActiveSubscription = false
  let userTrialEndingAt = null
  let trialUpgradeUrl = null

  if (currentUser?.subscriptions) {
    const activeSubscriptions = currentUser.subscriptions.filter(s => s.state == 'active')
    let trialSubscriptions = currentUser.subscriptions.filter(s => s.state == 'in_trial')
    userHasActiveSubscription = activeSubscriptions.length >= 1 || trialSubscriptions.length > 0
    if (activeSubscriptions.length == 0 && trialSubscriptions.length) {
      trialSubscriptions = sortBy(trialSubscriptions, (s: ScoSubscription) =>
        moment(s.trialEndedAt || s.currentPeriodEndsAt)
      )
      trialUpgradeUrl = trialSubscriptions[0]?.updatePaymentMethodLink
      userTrialEndingAt =
        !trialSubscriptions[0]?.cardLabel &&
        (trialSubscriptions[0]?.trialEndedAt || trialSubscriptions[0]?.currentPeriodEndsAt)
    }
  }

  const items: Array<SiteNavItem> = [
    {
      hideOnMobile: true,
      linkProps: {
        title: 'More',
        // Overview target for the drawer accordion's synthetic first child
        // (mapToSidebarItem); without a string href, Next.js Link throws.
        href: '/about',
      },
      activePaths: ['/about', '/workspaces/migration'],
      items: [
        {
          linkProps: {
            title: 'About',
            href: '/about',
          },
        },
        {
          linkProps: {
            title: 'Pricing',
            href: '/accelerator/plans',
          },
        },
        {
          linkProps: {
            title: 'Perks',
            href: '/perks',
          },
        },
        {
          linkProps: {
            title: 'Members',
            href: '/members',
          },
        },
        {
          linkProps: {
            title: 'Tools',
            href: '/tools',
          },
        },
        {
          linkProps: {
            title: 'Client Success',
            href: '/success',
          },
        },
        {
          linkProps: {
            title: 'Support',
            href: '/support',
          },
        },
        {
          linkProps: {
            opacity: 0.5,
            title: 'Terms of Service',
            href: '/legal/terms-of-service',
          },
        },
        {
          linkProps: {
            opacity: 0.5,
            title: 'Privacy Policy',
            href: '/legal/privacy-policy',
          },
        },
        {
          linkProps: {
            opacity: 0.5,
            title: 'Sitemaps',
            href: '/sitemaps',
          },
        },
      ],
    },
    {
      linkProps: {
        title: 'Help',
        // `pathname` can already carry a query (e.g. ?toolSessionId=…); append instead of adding a second `?`.
        href: `${pathname}${pathname?.includes('?') ? '&' : '?'}prompt=support`,
      },
    },

    // { dividerProps: {} },
    // {
    //   buttonProps: {
    //     bgColor: 'blackAlpha.500',
    //     border: '1px solid transparent',
    //     color: 'whiteAlpha.500',
    //     children: <LuSearch />,
    //     href: '/search',
    //     padding: 0,
    //     width: 'auto',
    //     variant: 'filled',
    //     _hover: {
    //       borderColor: 'whiteAlpha.200',
    //       color: 'white',
    //     },
    //   },
    // },
  ].filter(Boolean)

  if (currentUser?.isRegistered) {
    if (currentUser?.isRegistered) {
      items.push({
        render: () => <ChatSiteNavBarItem />,
      })
    }

    items.push({
      ...(userTrialEndingAt
        ? {
            badgeProps: {
              bgColor: 'orange.500',
              title: `${notificationCount} Notifications`,
              marginRight: '1em',
              children: <>Trial</>,
            },
          }
        : {}),
      ...(notificationCount
        ? {
            badgeProps: {
              title: `${notificationCount} Notifications`,
              marginRight: '1em',
              children: <>{notificationCount}</>,
            },
          }
        : {}),
      activePaths: ['/account'],
      buttonProps: {
        variant: 'subtle',
        bgColor: 'colorPalette.muted',
        href: '/account',
        size: 'sm',
        title: 'Account',
        borderRadius: 'full',
        paddingLeft: 0,
        paddingRight: getBreakpointValue([0, 0, null]),
        children: (
          <>
            <Avatar.Root size={'sm'}>
              {!!currentUser.pictureUrl && <Avatar.Image src={currentUser.pictureUrl} />}
              <Avatar.Fallback name={currentUser.fullName} />
            </Avatar.Root>
            {getBreakpointValue([
              null,
              null,
              <TextSpan key={'name'} color={'colorPalette.fg'} fontSize={'sm'} fontWeight={'600'}>
                {currentUser.fullName || currentUser.displayName}
              </TextSpan>,
            ])}
          </>
        ),
      },
      items: [
        ...(userTrialEndingAt
          ? [
              {
                customRender: () => (
                  <Box
                    bgColor={'colorPalette.emphasized'}
                    borderRadius={'md'}
                    fontSize={'14px'}
                    padding={'1em'}
                  >
                    <HStack>
                      <Icon as={LuClock} />{' '}
                      <Text>
                        {'Your trial ends '}
                        <strong>{moment(userTrialEndingAt).fromNow()}</strong>!
                      </Text>
                    </HStack>
                    {/* A card-less trial keeps access by adding a payment method, not by buying a
                        second plan: billing links to the Chargebee payment-method page. */}
                    <ButtonLink
                      href={trialUpgradeUrl || '/account/billing'}
                      marginTop={'1em'}
                      size={'sm'}
                      variant={'solid'}
                      width={'full'}
                    >
                      Upgrade Now
                    </ButtonLink>
                  </Box>
                ),
              },
              { dividerProps: { marginY: '0.25em' } },
            ]
          : []),
        ...(notificationCount
          ? [
              {
                linkProps: {
                  href: `/account/notifications`,
                  size: 'sm',
                  title: `${notificationCount} Notifications`,
                  children: (
                    <HStack align={'center'}>
                      <Text lineHeight={1}>Notifications</Text>
                      <Badge variant={'outline'}>
                        {notificationCount}
                        {' NEW'}
                      </Badge>{' '}
                    </HStack>
                  ),
                },
              },
              { dividerProps: { marginY: '0.25em' } },
            ]
          : []),
        ...(currentUser?.isAdmin
          ? [
              {
                linkProps: {
                  href: '/admin',
                  size: 'sm',
                  title: 'Admin',
                },
              },
              {
                linkProps: {
                  href: '/dev',
                  size: 'sm',
                  title: 'Developer',
                },
              },
            ]
          : []),

        {
          linkProps: {
            href: `/account`,
            size: 'sm',
            title: 'Manage Account',
          },
        },
        {
          linkProps: {
            href: `/account/support`,
            size: 'sm',
            title: 'Support & Feedback',
          },
        },
        {
          linkProps: {
            href: `/logout?returnTo=${pathname}`,
            size: 'sm',
            title: 'Logout',
            variant: 'outline',
          },
        },
      ].filter(Boolean) as Array<SiteNavItem>,
    })
  } else {
    items.push({
      linkProps: {
        href: `/login?returnTo=${pathname?.startsWith('/login') ? '/' : pathname}`,
        title: 'Login',
      },
      activePaths: ['/login'],
    })
  }

  if (!userHasActiveSubscription && !currentUser?.isAdmin) {
    items.push({
      buttonProps: {
        variant: 'outline',
        href:
          currentUser?.isRegistered && !userHasActiveSubscription
            ? '/accelerator/plans'
            : '/accelerator/start',
        marginLeft: [0, 0, 1],
        size: 'md',
        title: currentUser?.isRegistered && !userHasActiveSubscription ? 'Upgrade' : 'Get Started',
      },
    })
  }

  return items.filter(Boolean)
}
